import { memo, useCallback, useMemo, useState } from 'react'

import { orderBy, times } from 'lodash'

import { Skeleton } from '@mui/material'
import { Assignment, Close } from '@mui/icons-material'

import {
  Dialog,
  Button,
  IconButton,
  DialogContent,
  DialogTitle,
  Stack,
  Placeholder,
  DialogActions,
} from '@collaborate-health/uikit'

import { useDispatch, useSelector } from '@tabeeb/store'
import { Form, FormProps, IForm, FormElement, IAnswer, ControlElement } from '@collaborate-health/forms'
import { ContentFormsElementsRegistryContextProvider } from '@tabeeb/modules/forms/contexts'
import {
  ContentNamePartRequest,
  ControlOptionsResponse,
  ControlType,
  FormControlResponse,
  FormControlType,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { mapFormControlToControlElement } from '@tabeeb/modules/createFormPage/services/mapping'
import { formTitleFormatter, sessionTitleFormatter } from '@tabeeb/modules/shared/utils/text'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import domainService from '@tabeeb/services/domainService'

import { closeTenantFormsDialog, submitTenantFormAnswers } from '../../actions'
import { getDialogState } from '../../selectors'
import { useSnc } from '../../hooks'

import SNCQuestion from '../SncQuestion'

const supportedControls = [ControlType.Text, ControlType.Checkboxlist, ControlType.Selectlist]
const restrictedFormControlTypes = [FormControlType.PhoneNumber, FormControlType.Email]

const TenantFormsDialog: React.FC = () => {
  const dispatch = useDispatch()

  const currentUserId = useSelector<number>(getCurrentUserId)

  const { open, sncType, subdomain } = useSelector(getDialogState)

  const {
    loading,
    forms: sncForms,
    processing,
    onProcessAnswer,
    onReset,
  } = useSnc({ enabled: open, subdomain: subdomain ?? domainService.subdomain })

  const form = useMemo((): IForm => {
    const elements: FormElement[] = []

    let index = 0

    for (const sncForm of sncForms) {
      for (const control of orderBy(
        sncForm?.Controls?.filter(
          (c) => !restrictedFormControlTypes.includes(c.Type) && supportedControls.includes(c.ControlType)
        ) ?? [],
        (c) => c.Order
      )) {
        elements.push({
          ...mapFormControlToControlElement(control, sncForm?.Controls ?? []),
          dependencies: [],
          validators: [],
          displayLabel: false,
          isRequired: true,
          order: index++,
        })
      }
    }

    return {
      name: 'SNC',
      elements,
    }
  }, [sncForms])

  const [answers, setAnswers] = useState<IAnswer[]>([])

  useMemo(() => {
    const controlsIds = new Set(form.elements.filter((e) => e.elementType === 'control').map((e) => e.id))

    setAnswers((prevAnswers) => prevAnswers.filter((a) => controlsIds.has(a.controlId)))
  }, [form.elements])

  const onSubmitAnswer = useCallback(
    async (answer: IAnswer<unknown, ControlOptionsResponse>, control: ControlElement<FormControlResponse>) => {
      setAnswers((prev) => {
        const index = prev.findIndex((a) => a.controlId === answer.controlId)

        if (index === -1) {
          return [...prev, answer]
        }

        return [...prev.slice(0, index), answer, ...prev.slice(index + 1)]
      })

      if (control.meta?.ControlType === ControlType.Checkboxlist) {
        return
      }

      onProcessAnswer({
        FormControlId: control.meta?.Id!,
        FormId: control.meta?.FormId!,
        CheckedFormControlOptionId:
          answer.type === 'value'
            ? null
            : answer.type === 'option'
            ? answer.value?.meta?.Id!
            : answer.value[0]?.meta?.Id!,
        UserId: currentUserId,
        Value: answer.value?.toString(),
      })
    },
    [currentUserId, onProcessAnswer]
  )

  const onSubmit = useCallback(async () => {
    const description: string = form.elements
      .map((element) => {
        const answer = answers.find((a) => a.controlId === element.id)!

        if (answer.type === 'value') {
          return answer.value?.toString()
        }

        if (answer.type === 'option') {
          return answer.value.value
        }

        if (answer.type === 'options') {
          return answer.value.map((o) => o.value).join('_')
        }

        return ''
      })
      .join('_')

    const contentNameParts: ContentNamePartRequest[] = form.elements.map((element, index) => {
      const answer = answers.find((a) => a.controlId === element.id)!

      let value = ''

      if (answer.type === 'value') {
        value = answer.value?.toString()
      }

      if (answer.type === 'option') {
        value = answer.value.value
      }

      if (answer.type === 'options') {
        value = answer.value.map((o) => o.value).join(';')
      }

      return {
        Order: index,
        Question: element.name!,
        Answer: value,
      }
    })

    dispatch(
      submitTenantFormAnswers({
        description,
        contentNameParts,
      })
    )
  }, [answers, dispatch, form.elements])

  const onClose = useCallback(() => {
    dispatch(closeTenantFormsDialog())
  }, [dispatch])

  const noEmptyAnswers = useMemo(() => {
    return answers.every((a) => {
      if (a.type === 'value') {
        return !!a.value
      }

      if (a.type === 'option') {
        return !!a.value
      }

      if (a.type === 'options') {
        return !!a.value.length
      }

      return false
    })
  }, [answers])

  return (
    <Dialog
      open={open}
      maxWidth='xs'
      fullWidth
      onClose={onClose}
      TransitionProps={{
        onExited: () => {
          setAnswers([])
          onReset()
        },
      }}
    >
      <DialogTitle>
        {sessionTitleFormatter.format(sncType ?? 'Create Session')}
        <Stack position='absolute' right={14} top={10}>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Stack spacing={2}>
            {times(3, (index) => (
              <Stack key={index} spacing={1}>
                <Skeleton variant='text' width='75%' />
                <Skeleton variant='rounded' width='100%' height={40} />
              </Stack>
            ))}
          </Stack>
        ) : !form.elements.length ? (
          <Stack minHeight={200}>
            <Placeholder
              icon={Assignment}
              title={formTitleFormatter.format('There are no forms with the valid controls attached to the tenant')}
            />
          </Stack>
        ) : (
          <ContentFormsElementsRegistryContextProvider>
            <Form
              form={form}
              answers={answers}
              readOnly={processing}
              submitMode='perAnswer'
              wrapper={SNCQuestion}
              onSubmitAnswer={onSubmitAnswer as FormProps['onSubmitAnswer']}
            />
          </ContentFormsElementsRegistryContextProvider>
        )}
      </DialogContent>
      <DialogActions>
        {!!form.elements.length && (
          <Button
            loading={processing || loading}
            disabled={!noEmptyAnswers || form.elements.length !== answers.length}
            fullWidth
            size='small'
            variant='contained'
            color='primary'
            onClick={onSubmit}
          >
            {sessionTitleFormatter.format(sncType ?? 'Create Session')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default memo(TenantFormsDialog)

